import {Button, useNavigate} from '@startlibs/components'
import {callIfFunction} from '@startlibs/utils'
import {RadioboxGroup, SimpleRadiobox} from '@startlibs/form'
import React from 'react'
import {ConfirmDialog, useConfirmDialog} from '../../hooks/useConfirmDialog'
import { ExpertCaseCard } from '../ExpertCaseCard';
import {getJwt} from '../../hooks/useJwt'
import {jwtPostFetcher} from '../../utils/authFetch'
import {responseFailure} from '../../utils/validation'
import {useConfirmAnswers} from '../customReport/useConfirmAnswers'
import {FormattedMessage, useIntl} from "react-intl";
import { lazyProviderInfoSec } from '../../components/WithProvider';


export const useSubmitCase = (caseRequest, values, onSuccess) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const providerInfoSec = lazyProviderInfoSec.read()
  const isSignatureRequired = providerInfoSec?.reportSignatureRequired || false
  const confirmAnswers = useConfirmAnswers(caseRequest, true)

  const confirmMissingSignatureDialog = useConfirmDialog(
    <ConfirmDialog
      title={intl.formatMessage({
        defaultMessage: "Signature required",
        description: "Submit case review signature required dialog title"
      })}
      onSuccess={() => navigate(`/expert/case/${caseRequest.requestId}/questions#signature`)}
      confirm={<Button highlight><FormattedMessage
        defaultMessage="Add signature"
        description="Submit case review signature required dialog button"
      /></Button>}
      closeLabel={intl.formatMessage({
        defaultMessage: "Cancel",
        description: "Cancel Button"
      })}
    >
      <p><FormattedMessage
        defaultMessage="You must sign the review before proceeding."
        description="Submit case review signature required dialog message"
      /></p>
    </ConfirmDialog>
  )

  return useConfirmDialog(<ConfirmDialog
      values={{useCustomReport: !!caseRequest.report?.useCustomReport}}
      title={intl.formatMessage({
        defaultMessage: "Submit case review",
        description: "Submit case review dialog title"
      })}
      confirm={<Button highlight><FormattedMessage
        defaultMessage="Submit"
        description="Submit case review dialog submit button"
      /></Button>}
      preValidation={({useCustomReport}) => {
        if (isSignatureRequired && !useCustomReport && (!caseRequest.caseInfo.signatures || caseRequest.caseInfo.signatures.length === 0)) {
          confirmMissingSignatureDialog()
          return new Promise(() => {})
        }
        return useCustomReport ? Promise.resolve() : confirmAnswers()
      }}
      action={({useCustomReport = false}) =>
        jwtPostFetcher(getJwt())(`/api/reviewer/submitReview?requestId=${caseRequest.requestId}&useCustomReport=${useCustomReport}`, callIfFunction(values))
      }
      onSuccess={onSuccess}
      onFailure={responseFailure((n,{status}) => status === 401 && intl.formatMessage({
        defaultMessage: "Access denied",
        description: "Submit case review dialog access denied error"
      }))}
    closeLabel={intl.formatMessage({
      defaultMessage:"Cancel",
      description: "Cancel Button"
    })}
    >
      <p><FormattedMessage
        defaultMessage="Your review of the following case will be shared with your administrator:"
        description="Submit case review dialog info"
      /></p>
      <ExpertCaseCard caseRequest={caseRequest}/>

    {
      caseRequest.report?.customReport && <>
        <p><FormattedMessage
          defaultMessage="A custom report was uploaded. Would you like to use it as the final report?"
          description="Submit case review dialog custom report question"
        /></p>
        <RadioboxGroup path="useCustomReport" framedBoxes highlightSelected horizontal wide css="margin-bottom:1.5rem">
        <SimpleRadiobox
          fieldValue={false}
          label={intl.formatMessage({
            defaultMessage: "Use default report",
            description: "Submit case review dialog use default report option"
          })}
        />
        <SimpleRadiobox
          fieldValue={true}
          label={intl.formatMessage({
            defaultMessage: "Use custom report",
            description: "Submit case review dialog use custom report option"
          })}
        />
      </RadioboxGroup>
      </>
    }
    <FormattedMessage
      defaultMessage={`<p>
        You will no longer be able to edit this case, unless you are
        requested to provide additional information.
      </p>
      <p>Are you sure you want to proceed?</p>`}
      description="Submit case review dialog confirmation"
      values={{p: str => <p>{str}</p>}}
      />
    </ConfirmDialog>
  )
}
